import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { PICKUP_TYPES } from '@/components/ShipmentForm/constants/dropoffOptions';
import { useFormatters } from './hooks';
import { HR, Header1, Body1, Tag, Step, SubStepDesktop, SubStepMobile, Anchor } from './blocks';
import Dropoff from './Dropoff';
import { advance } from '@onward-delivery/core';
import { WAREHOUSE_LOAD } from './itemEvents';

function Pickup({ isDesktop, isLoading, warehouse, before, after, order, callbacks, readOnly }) {
    const CROSSDOCK_PICKUP_STEPS = useMemo(() => {
        return [
            ...(isDesktop
                ? [
                      {
                          description: '1. Set warehouse pickup and customer drop off date and time',
                          complete: ({ order, before, after }) => {
                              const isValid = (obj, field) => obj?.[field] && obj?.[field].length > 0;
                              return isValid(before, 'est_pickup_date');
                          },
                          ...(!readOnly
                              ? {
                                    cta: 'Complete Task',
                                    callback: () => {
                                        callbacks.editCrossdock({ event: before, stage: 'CUSTOMER' });
                                    },
                                }
                              : {}),
                      },
                  ]
                : []),
            {
                description: isDesktop ? (
                    `2. Pick up items from warehouse and update status to 'In-transit'`
                ) : (
                    <>
                        <Grid direction="row">
                            <Body1>{before.location ? before.location.location_name : '--'}</Body1>
                        </Grid>
                        <Grid direction="row">
                            <Body1>{before.location ? before.location.address : '--'}</Body1>
                        </Grid>
                        <Grid direction="row">
                            <Body1>
                                {`${before?.location?.location_type || '--'}, ${
                                    (PICKUP_TYPES[before?.location?.location_type] || []).find(
                                        (o) => o.value === before?.location?.location_info
                                    )?.label || '--'
                                }`}
                            </Body1>
                        </Grid>
                    </>
                ),
                complete: ({ order, before, after }) => {
                    return after?.pickup_date;
                },
                ...(!readOnly
                    ? {
                          cta: isDesktop ? 'Mark In-transit' : 'Arrived',
                          callback: ({ order, before, after }) => {
                              const NOW = new Date();
                              const prompt = (
                                  <>
                                      <Grid
                                          direction="row"
                                          container
                                          css={css`
                                              margin-bottom: 12px;
                                          `}
                                      >
                                          <Body1>Are you sure you want to mark the order as 'In-Transit'?</Body1>
                                      </Grid>
                                      <Grid direction="row" container>
                                          <Body1>
                                              This will let the customer and shipper know the order is on the way
                                          </Body1>
                                      </Grid>
                                  </>
                              );

                              // Phase 3 of the decouple-order-route-mappings project: replace
                              // the legacy direct emit of CD_PENDING:CONFIRM (which used the
                              // pre-project shortcut CD_PENDING -> CD_COMPLETE) with an
                              // advance() walk along the canonical chain
                              // CD_PENDING -> CD_RECEIVED -> CD_COMPLETE. The "Mark In-transit"
                              // button now lands the order in CD_COMPLETE via the full chain so
                              // the wh_event audit log reflects the warehouse-receipt step.
                              // CD_COMPLETE means "in transit / out for delivery" — the button
                              // text is correct even though the state name suggests otherwise.
                              const advanceEvents = advance(order, order.event_state, 'CD_COMPLETE');
                              const callback = (_orderUpdates, itemTrackingEvents) =>
                                  callbacks.upsertEvent({
                                      events: advanceEvents,
                                      order_id: order.order_id,
                                      order: {
                                          order_status: 'inProgress',
                                          ltl_pickup_completed: NOW.toISOString(),
                                      },
                                      trackingEvents: itemTrackingEvents || [],
                                  });

                              callbacks.openChecklist(
                                  {
                                      content: prompt,
                                      documents: [
                                          { field: 'proof_of_pickup', name: 'Proof of Pickup', required: false },
                                      ],
                                      signatures: [],
                                      title: 'Pick Up Order',
                                      // "Pick up items from warehouse" — a departure, not an origin
                                      // collection.
                                      ...WAREHOUSE_LOAD,
                                      itemsHelperText: 'Checkmark each item after loading.',
                                  },
                                  callback
                              );
                          },
                      }
                    : {}),
            },
        ];
    }, [isDesktop, before]);

    const formatters = useFormatters(order);
    const isComplete = useMemo(() => {
        return CROSSDOCK_PICKUP_STEPS.map((step) => {
            return step.complete({ order, before, after, warehouse });
        });
    }, [before, after, order, warehouse]);

    const inProgressIdx = useMemo(() => {
        return isComplete.findIndex((x) => !x);
    }, [isComplete]);

    const allComplete = isComplete.every((x) => x);
    const isReceivingOrder = useMemo(() => {
        return !!(order?.wh_events || []).find((e) => e.transition?.next === 'RECEIVING');
    }, [order]);

    return (
        <Step
            isDesktop={isDesktop}
            isComplete={allComplete}
            inProgress={!allComplete && before.status === 'RECEIVED'}
            label={() => {
                return (
                    <Grid
                        direction="row"
                        container
                        css={css`
                            align-items: center;
                        `}
                    >
                        <Grid
                            direction="column"
                            css={css`
                                margin-right: 8px;
                            `}
                        >
                            <Header1>Cross-dock Picked up</Header1>
                        </Grid>
                        {isDesktop || allComplete ? (
                            <Grid
                                direction="column"
                                css={css`
                                    margint-right: 8px;
                                `}
                            >
                                <Tag>
                                    {isDesktop ? (
                                        <Body1
                                            css={css`
                                                margin-right: 20px;
                                            `}
                                        >{`${isReceivingOrder ? 'Scheduled Load Date' : 'Scheduled Pickup'}: ${
                                            before.est_pickup_date
                                                ? formatters.dateShort.format(new Date(before.est_pickup_date))
                                                : 'Pending'
                                        }`}</Body1>
                                    ) : null}
                                    <Body1
                                        css={css`
                                            margin-right: 20px;
                                        `}
                                    >{`Completed: ${
                                        after?.pickup_date
                                            ? formatters.dateShort.format(new Date(after.pickup_date))
                                            : 'Pending'
                                    }`}</Body1>
                                    {isDesktop
                                        ? !readOnly && (
                                              <Anchor
                                                  onClick={() => {
                                                      callbacks.editCrossdock({ event: before, stage: 'CUSTOMER' });
                                                  }}
                                              >
                                                  <Header1>Edit</Header1>
                                              </Anchor>
                                          )
                                        : null}
                                </Tag>
                            </Grid>
                        ) : null}
                    </Grid>
                );
            }}
        >
            {() => (
                <>
                    {CROSSDOCK_PICKUP_STEPS.map(({ description, cta, callback }, idx) => {
                        const SubStep = isDesktop ? SubStepDesktop : SubStepMobile;

                        return (
                            <SubStep
                                key={idx}
                                isComplete={isComplete[idx]}
                                inProgress={inProgressIdx === idx}
                                isLoading={isLoading}
                                isLast={idx === CROSSDOCK_PICKUP_STEPS.length - 1}
                                description={description}
                                cta={cta}
                                secondaryCta={null}
                                callback={
                                    callback
                                        ? () => {
                                              callback({ before, after, order });
                                          }
                                        : null
                                }
                                secondaryCallback={null}
                            />
                        );
                    })}
                </>
            )}
        </Step>
    );
}

export default function CrossdockPickup({ isDesktop, ...rest }) {
    return (
        <>
            <Pickup {...rest} isDesktop={isDesktop} />
            {isDesktop ? <Dropoff {...rest} isDesktop={isDesktop} /> : null}
        </>
    );
}
